import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { User } from "../types/user";
import { getUsers } from "../lib/api/users";
import { useAuth } from "./AuthContext";
import toast from "react-hot-toast";

// Event listener types
type UserEventType = "settingsUpdated" | "availabilityChanged";
type UserEventListener = (data: any) => void;

interface UserState {
  currentUser: User | null;
  colleagues: User[];
  isLoadingUsers: boolean;
  error: string | null;
  refreshUsers: () => Promise<void>;
  updateUserSettings: (userId: string, settings: any, app?: any) => void;
  updateCurrentUser: (user: User) => void;
  // Event emitter methods
  on(event: UserEventType, listener: UserEventListener): void;
  off(event: UserEventType, listener: UserEventListener): void;
  emit(event: UserEventType, data: any): void;
}

const UserContext = createContext<UserState>({
  currentUser: null,
  colleagues: [],
  isLoadingUsers: true,
  error: null,
  refreshUsers: async () => {},
  updateUserSettings: () => {},
  updateCurrentUser: () => {},
  on: () => {},
  off: () => {},
  emit: () => {},
});

export function UserProvider({ children }: { children: React.ReactNode }) {
  const { token } = useAuth();
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [colleagues, setColleagues] = useState<User[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Event emitter implementation using refs
  const listenersRef = useRef<Map<UserEventType, Set<UserEventListener>>>(new Map());

  // Event emitter methods
  const on = useCallback((event: UserEventType, listener: UserEventListener) => {
    if (!listenersRef.current.has(event)) {
      listenersRef.current.set(event, new Set());
    }
    listenersRef.current.get(event)!.add(listener);
  }, []);

  const off = useCallback((event: UserEventType, listener: UserEventListener) => {
    const listeners = listenersRef.current.get(event);
    if (listeners) {
      listeners.delete(listener);
    }
  }, []);

  const emit = useCallback((event: UserEventType, data: any) => {
    const listeners = listenersRef.current.get(event);
    if (listeners) {
      listeners.forEach(listener => listener(data));
    }
  }, []);

  // Load users data
  const loadUsers = useCallback(async () => {
    if (!token) {
      setIsLoadingUsers(false);
      return;
    }

    setIsLoadingUsers(true);
    setError(null);

    try {
      const users = await getUsers();
      const userEmail = localStorage.getItem("userEmail");
      const user = users.find((u: User) => u.email === userEmail);

      if (!user) {
        throw new Error("Current user not found");
      }

      // Set current user
      setCurrentUser(user);
      
      // Filter out admin users from colleagues list
      const filteredColleagues = users.filter((u: User) => 
        u.id !== user.id && u.role !== "admin"
      );
      setColleagues(filteredColleagues);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to load users";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoadingUsers(false);
    }
  }, [token]);

  // Update user settings without full reload
  const updateUserSettings = useCallback((userId: string, settings: any, app?: any) => {
    if (currentUser && currentUser.id === userId) {
      setCurrentUser(prev => prev ? { ...prev, settings, app: app || prev.app } : prev);
    }
    
    // Also update in colleagues list
    setColleagues(prev => 
      prev.map(colleague => 
        colleague.id === userId 
          ? { ...colleague, settings, app: app || colleague.app }
          : colleague
      )
    );
  }, [currentUser]);

  // Allow external updates to current user
  const updateCurrentUser = useCallback((user: User) => {
    setCurrentUser(user);
  }, []);

  // Public method to refresh users
  const refreshUsers = useCallback(async () => {
    await loadUsers();
  }, [  on,
        off,
        emit,
      loadUsers]);

  // Load users on mount and when token changes
  useEffect(() => {
    if (token) {
      loadUsers();
    } else {
      // Reset state when no token
      setCurrentUser(null);
      setColleagues([]);
      setIsLoadingUsers(false);
      setError(null);
    }
  }, [token, loadUsers]);

  return (
    <UserContext.Provider
      value={{
        currentUser,
        colleagues,
        isLoadingUsers,
        error,
        refreshUsers,
        updateUserSettings,
        updateCurrentUser,
        on,
        off,
        emit,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

export function useUsers() {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUsers must be used within a UserProvider");
  }
  return context;
}
